import { Button } from '@base-ui/react/button';
import { Input } from '@base-ui/react/input';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { DraftAncestorNotice } from '~/components/ui/DraftAncestorNotice';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { BackLink } from '~/components/ui/NavLinks';
import { PageTitle } from '~/components/ui/Page';
import { RichTextEditor } from '~/components/ui/RichTextEditor';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import buttonStyles from '~/styles/button.module.css';

type TopicAdmin = components['schemas']['TopicAdmin'];

export const Route = createFileRoute('/_admin/topics/$topicId')({
  component: TopicDetail,
});

function TopicDetail() {
  const { topicId } = Route.useParams();
  const topicIdNum = Number(topicId);
  const [editing, setEditing] = useState(false);

  const admin = api.adminGetTopic.useQuery({ path: { topic_id: topicIdNum } });

  if (admin.isLoading) return <Message>Loading topic...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading topic</Message>;

  const contentHtml = admin.data.content;

  return (
    <Container>
      <BackLink>
        <Link to="/topics" search={{ lesson: admin.data.lesson_id }}>
          &larr; Back to topics
        </Link>
      </BackLink>

      <Header>
        <PageTitle>{admin.data.title}</PageTitle>
        {!editing && (
          <Button
            className={buttonStyles.Button}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
      </Header>

      {editing ? (
        // key remounts the form when switching topics, so its seeded state stays fresh.
        <TopicEditor
          key={admin.data.id}
          topic={admin.data}
          onDone={() => setEditing(false)}
        />
      ) : (
        <>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{admin.data.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{admin.data.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{admin.data.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{admin.data.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Lesson</FieldLabel>
                <FieldValue>
                  <Link
                    to="/lessons/$lessonId"
                    params={{ lessonId: String(admin.data.lesson_id) }}
                  >
                    #{admin.data.lesson_id}
                  </Link>
                </FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Content</SectionTitle>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: rendering trusted topic HTML in admin */}
            <ContentBody dangerouslySetInnerHTML={{ __html: contentHtml }} />
          </Section>
        </>
      )}
    </Container>
  );
}

function TopicEditor({
  topic,
  onDone,
}: {
  topic: TopicAdmin;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(topic.title);
  const [slug, setSlug] = useState(topic.slug);
  const [order, setOrder] = useState(String(topic.order));
  const [status, setStatus] = useState(topic.status);
  const [content, setContent] = useState(topic.content);

  const invalidateTopic = () => {
    queryClient.invalidateQueries({
      queryKey: api.adminGetTopic.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.topicDetail.getQueryKey({ path: { topic_id: topic.id } }),
    });
    queryClient.invalidateQueries({
      queryKey: api.lessonTopics.getQueryKey({
        path: { lesson_id: topic.lesson_id },
      }),
    });
  };

  const updateMutation = api.adminUpdateTopic.useMutation(undefined, {
    onSuccess: () => {
      invalidateTopic();
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteTopic.useMutation(undefined, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: api.lessonTopics.getQueryKey({
          path: { lesson_id: topic.lesson_id },
        }),
      });
      navigate({ to: '/topics', search: { lesson: topic.lesson_id } });
    },
  });

  const trimmedTitle = title.trim();

  const handleSave = () => {
    if (!trimmedTitle) return;
    updateMutation.mutate({
      path: { topic_id: topic.id },
      body: {
        title: trimmedTitle,
        slug,
        order: Number(order),
        status,
        content,
      },
    });
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this topic? This cannot be undone.')) return;
    deleteMutation.mutate({ path: { topic_id: topic.id } });
  };

  return (
    <Section>
      <SectionTitle>Edit topic</SectionTitle>
      <FormGrid>
        <FormRow>
          <FormLabel htmlFor="topic-title">Title</FormLabel>
          <TextInput
            id="topic-title"
            value={title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="topic-slug">Slug</FormLabel>
          <TextInput
            id="topic-slug"
            value={slug}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSlug(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="topic-order">Order</FormLabel>
          <TextInput
            id="topic-order"
            type="number"
            value={order}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setOrder(e.target.value)
            }
          />
        </FormRow>
        <FormRow>
          <FormLabel htmlFor="topic-status">Status</FormLabel>
          <StatusSelect
            id="topic-status"
            value={status}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setStatus(e.target.value as TopicAdmin['status'])
            }
          >
            <option value="draft">draft</option>
            <option value="published">published</option>
          </StatusSelect>
          <DraftAncestorNotice
            entityLabel="topic"
            ancestors={[
              { label: 'lesson', status: topic.lesson_status },
              { label: 'course', status: topic.course_status },
              { label: 'series', status: topic.series_status },
            ]}
          />
        </FormRow>
        <FormRow>
          <FormLabel>Content</FormLabel>
          <RichTextEditor
            value={topic.content}
            onChange={setContent}
            ariaLabel="Topic content"
          />
        </FormRow>
      </FormGrid>

      {updateMutation.isError && (
        <Message $error>Could not save changes. Please try again.</Message>
      )}
      {deleteMutation.isError && (
        <Message $error>
          Could not delete — the topic may still have questions.
        </Message>
      )}

      <Actions>
        <Button
          className={buttonStyles.Button}
          onClick={handleSave}
          disabled={updateMutation.isPending || !trimmedTitle}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save'}
        </Button>
        <Button className={buttonStyles.Button} onClick={onDone}>
          Cancel
        </Button>
        <DeleteButton
          className={buttonStyles.Button}
          onClick={handleDelete}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
        </DeleteButton>
      </Actions>
    </Section>
  );
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--component-margin);
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: var(--inner-padding);
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const FormLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--grey-700);
`;

const fieldStyles = `
  box-sizing: border-box;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--grey-300);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: var(--grey-50);
  color: var(--grey-900);

  &:focus {
    outline: 2px solid var(--blue);
    outline-offset: -1px;
  }
`;

const TextInput = styled(Input)`
  ${fieldStyles}
`;

const StatusSelect = styled.select`
  ${fieldStyles}
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: var(--inner-padding);
`;

const DeleteButton = styled(Button)`
  margin-left: auto;
  color: var(--error);
`;

const ContentBody = styled.div`
  padding: var(--inner-padding);
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--grey-900);

  p {
    margin: 0 0 1rem;
  }

  h1,
  h2,
  h3,
  h4 {
    margin: 1.5rem 0 0.75rem;
    font-weight: 600;
    color: var(--grey-900);
  }
  h1 {
    font-size: 1.25rem;
  }
  h2 {
    font-size: 1.125rem;
  }
  h3 {
    font-size: 1rem;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
    margin: 1rem auto;
  }

  blockquote {
    margin: 1rem 0;
    padding: 0.5rem 1rem;
    border-left: 3px solid var(--grey-300);
    background: var(--grey-75);
    color: var(--grey-700);
    font-style: italic;

    p {
      margin: 0 0 0.5rem;
    }
    p:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  a {
    color: var(--blue);
  }
`;

const Container = styled.div``;

const Message = styled.div<{ $error?: boolean }>`
  padding: 2rem;
  text-align: center;
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
`;
